import React, { Suspense, useEffect } from 'react';
import { NavLink, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../auth/AuthContext';
import { hasAnyRole, roleLabelFor } from '../auth/roles';
import { paths } from '../routes/paths';
import PageLoader from '../components/PageLoader';
import AccountMenu from '../components/AccountMenu';
import { initials } from '../pages/admin/ProfileMenu';
import { ToastProvider } from '../pages/admin/Toast';
import { PANEL_ICONS } from './panelIcons';
import '../pages/admin/admin.css';

/**
 * The shell shared by every signed-in panel (admin and organiser): sidebar, top bar with the profile
 * menu, and the page outlet. A panel is just configuration:
 *   label        small caption above the menu ("ADMIN", "ORGANISER")
 *   sections     [{ key, label, path, end?, soon?, roles?, titleFor?(pathname) }] in sidebar order (the same list also builds the routes)
 *   profilePath  where "My profile" lives
 *   identity     optional { name, subtitle } for the card at the foot of the sidebar (default: the user's name and role)
 * Access control is done by <RequireRole> around the layout in AppRoutes; a section's own `roles`
 * only decides whether its menu entry is shown (its route is guarded in routes/sectionRoutes.js).
 */
export default function PanelLayout({ label, sections, profilePath, fallbackTitle, identity }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // On phones the sidebar is a horizontally scrolling strip; keep the active link visible.
  useEffect(() => {
    const strip = document.querySelector('.adm-sidebar');
    const active = strip && strip.querySelector('.adm-nav.on');
    if (active && strip.scrollWidth > strip.clientWidth) {
      strip.scrollTo({ left: active.offsetLeft - strip.clientWidth / 2 + active.offsetWidth / 2, behavior: 'auto' });
    }
  }, [location.pathname]);

  const logout = () => {
    signOut();
    navigate(paths.login, { replace: true });
  };

  const visible = sections.filter((s) => !s.roles || hasAnyRole(user, s.roles));
  const onProfile = location.pathname.startsWith(profilePath);
  // The most specific section wins, so /company-profile/verification is titled "Company profile".
  const current = [...sections]
    .filter((s) => location.pathname === s.path || location.pathname.startsWith(`${s.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  const title = onProfile ? 'My profile' : current?.titleFor?.(location.pathname) || current?.label || fallbackTitle;
  const roleLabel = roleLabelFor(user);
  const who = identity || { name: user.fullName, subtitle: roleLabel };

  return (
    <ToastProvider>
      <div className="adm-layout">
        <aside className="adm-sidebar">
          <Link to={paths.home} className="adm-logo-link" title="View site" aria-label="Events Ignite, view site">
            <Logo alt="Events Ignite" className="adm-logo" />
          </Link>
          <div className="adm-label">{label}</div>
          {visible.map((s) => (
            <NavLink key={s.key} to={s.path} end={s.end} className={({ isActive }) => (isActive ? 'adm-nav on' : 'adm-nav')}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d={PANEL_ICONS[s.key]} /></svg>
              {s.label}
              {s.soon && <span className="adm-soon">Soon</span>}
            </NavLink>
          ))}
          <NavLink to={profilePath} className={({ isActive }) => (isActive ? 'adm-user on' : 'adm-user')} aria-label="Open my profile">
            <div className="adm-avatar">{initials(who.name)}</div>
            <div>
              <b>{who.name}</b>
              <span>{who.subtitle}</span>
            </div>
          </NavLink>
        </aside>
        <div className="adm-body">
          <header className="adm-topbar">
            <h1>{title}</h1>
            <div className="adm-top-actions">
              <ThemeToggle />
              <AccountMenu user={user} onLogout={logout} />
            </div>
          </header>
          <main className="adm-main">
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
